import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Tooltip } from '@mantine/core';
import {
	IconArrowBackUp,
	IconArrowForwardUp,
	IconDeviceDesktop,
	IconDeviceMobile,
} from '@tabler/icons-react';
import styled from 'styled-components';

import { GhostIconButton } from './ghostIconButton.styled';

import type { EditorTab } from './EmailTemplateEditorForm.types';
import type { PreviewDevice } from './EmailPreview.types';

interface Props {
	tab: EditorTab;
	device: PreviewDevice;
	canUndo: boolean;
	canRedo: boolean;
	onTabChange: (tab: EditorTab) => void;
	onDeviceChange: (device: PreviewDevice) => void;
	onUndo: () => void;
	onRedo: () => void;
}

const Spacer = styled.div`
	flex: 1;
`;

const Segmented = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.125rem;
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-md);
`;

const SegmentedItem = styled.button<{ $active: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	height: 1.5rem;
	padding: 0 0.75rem;
	border: 1px solid ${({ $active }) => ($active ? 'var(--mantine-color-blue-6)' : 'transparent')};
	border-radius: var(--mantine-radius-sm);
	background-color: ${({ $active }) => ($active ? 'var(--mantine-color-blue-0)' : 'transparent')};
	color: ${({ $active }) =>
		$active ? 'var(--mantine-color-text)' : 'var(--mantine-color-gray-9)'};
	font-size: 0.75rem;
	font-weight: ${({ $active }) => ($active ? 500 : 400)};
	cursor: pointer;
	transition:
		background-color 100ms ease-out,
		color 100ms ease-out;

	&:hover {
		background-color: ${({ $active }) =>
			$active ? 'var(--mantine-color-blue-0)' : 'var(--mantine-color-gray-0)'};
	}
`;

const DeviceGroup = styled(Segmented)`
	margin-left: 0.5rem;
`;

export const EditorToolbar = ({
	tab,
	device,
	canUndo,
	canRedo,
	onTabChange,
	onDeviceChange,
	onUndo,
	onRedo,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const undoLabel = tMipManage('email_templates.editor.undo', { defaultValue: 'Undo' });
	const redoLabel = tMipManage('email_templates.editor.redo', { defaultValue: 'Redo' });

	return (
		<>
			<Tooltip label={undoLabel} withArrow>
				<GhostIconButton
					type="button"
					disabled={!canUndo}
					aria-label={undoLabel}
					onClick={onUndo}
				>
					<IconArrowBackUp size={18} />
				</GhostIconButton>
			</Tooltip>
			<Tooltip label={redoLabel} withArrow>
				<GhostIconButton
					type="button"
					disabled={!canRedo}
					aria-label={redoLabel}
					onClick={onRedo}
				>
					<IconArrowForwardUp size={18} />
				</GhostIconButton>
			</Tooltip>

			{tab === 'preview' && (
				<DeviceGroup role="group">
					<SegmentedItem
						type="button"
						$active={device === 'desktop'}
						onClick={() => onDeviceChange('desktop')}
					>
						<IconDeviceDesktop size={14} />
						{tMipManage('email_templates.preview.device.desktop')}
					</SegmentedItem>
					<SegmentedItem
						type="button"
						$active={device === 'mobile'}
						onClick={() => onDeviceChange('mobile')}
					>
						<IconDeviceMobile size={14} />
						{tMipManage('email_templates.preview.device.mobile')}
					</SegmentedItem>
				</DeviceGroup>
			)}

			<Spacer />

			<Segmented role="group">
				<SegmentedItem
					type="button"
					$active={tab === 'edit'}
					onClick={() => onTabChange('edit')}
				>
					{tMipManage('email_templates.editor.tabs.editor', { defaultValue: 'Editor' })}
				</SegmentedItem>
				<SegmentedItem
					type="button"
					$active={tab === 'preview'}
					onClick={() => onTabChange('preview')}
				>
					{tMipManage('email_templates.editor.tabs.preview')}
				</SegmentedItem>
			</Segmented>
		</>
	);
};
